import { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { PresetButtonColors } from '@tinderapp/ui-react';
import classNames from 'classnames';

import { CLASSNAME_LANGUAGE_PICKER } from 'Config/configs/prerender';

import { openModal } from 'Store/modals/actions';
import { MODAL_LANGUAGE_SELECTION } from 'Store/modals/constants';

import { VARIANT_STATIC } from 'Containers/PageLayout/Header/lib/constants';
import type { HeaderVariant } from 'Containers/PageLayout/Header/types';
import Button from 'Components/Obsidian/Button';

import LanguageIcon from 'Assets/reactSvgs/LanguageIcon';

import { supportedLanguagesAsArray } from './utils';

type Props = {
  className?: string;
  showLabel?: boolean;
  showIcon?: boolean;
  variant?: HeaderVariant;
  navigate?: (b: string, a: any) => void;
  tabIndex?: number;
};

const LanguagePicker = ({
  className,
  showLabel = true,
  showIcon = true,
  navigate,
  variant = VARIANT_STATIC,
  tabIndex = 0,
}: Props) => {
  const dispatch = useDispatch();
  const { formatMessage, formatDisplayName, locale } = useIntl();
  const id = `language-picker-${locale}`;

  const handleClick = useCallback(() => {
    dispatch(
      openModal(MODAL_LANGUAGE_SELECTION, {
        navigate,
      })
    );
  }, [dispatch, navigate]);

  const currentLanguage = supportedLanguagesAsArray.find((item) => item.value === locale);

  const buttonColors =
    variant === VARIANT_STATIC ? PresetButtonColors.Tertiary : PresetButtonColors.TertiaryOverlay;

  return (
    <Button
      {...buttonColors}
      className={classNames(CLASSNAME_LANGUAGE_PICKER, className)}
      onClick={handleClick}
      title={formatMessage({ id: 'language' })}
      data-testid="language-picker"
      aria-label={
        currentLanguage?.value
          ? formatMessage({ id: 'chooseALanguage' })
          : formatMessage({ id: 'language' })
      }
      aria-describedby={id}
      lang={currentLanguage?.value}
      size="medium"
      tabIndex={tabIndex}
    >
      {showIcon ? <LanguageIcon className="Sq(16px) Op(.9) Va(m) Mend(8px) Va(m)" fill="" /> : null}
      {showLabel ? (
        <span className="P(0) D(ib) Va(m)">{formatMessage({ id: 'language' })}</span>
      ) : null}
      <span id={id} className="Hidden" aria-hidden>
        {`${formatMessage({ id: 'language' })} (${formatDisplayName(currentLanguage?.value ?? '', {
          type: 'language',
        })})`}
      </span>
    </Button>
  );
};

export default memo(LanguagePicker);
